"use client";

import React from "react";
import { Heart, Users, Sparkles, PenTool, Palette, Activity, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benefitsData = [{
  id: "healing",
  title: "Emotional Resilience",
  description: "Navigate complex feelings in a safe, trauma-informed space designed to foster long-term emotional healing.",
  icon: Heart
}, {
  id: "connection",
  title: "Family Connection",
  description: "Strengthen the bonds between children and caregivers through shared creative exploration and understanding.",
  icon: Users
}, {
  id: "agency",
  title: "Personal Agency",
  description: "Empower children to reclaim their narrative and discover the profound purpose within their unique voice.",
  icon: Sparkles
}];
const mediumsData = [{
  id: "written",
  label: "Written Word",
  title: "Guided Storytelling",
  description: "Collaborate with our gentle coaches to transform inner experiences into words. Through journaling and structured narrative, writing helps organize thoughts and externalize heavy burdens.",
  icon: PenTool,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570824/img/guided_writing_session.png"
}, {
  id: "visual",
  label: "Visual Arts",
  title: "Creative Expression",
  description: "When words feel out of reach, express complex emotions through painting and mixed media. Our art workshops provide a tactile, colorful way to explore and release internal struggles.",
  icon: Palette,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570824/img/creative_arts_painting.png"
}, {
  id: "movement",
  label: "Movement",
  title: "Expressive Movement",
  description: "Release tension and tell stories through the body. Our movement sessions combine gentle choreography and free expression to help process trauma that the mind cannot yet speak.",
  icon: Activity,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570824/img/expressive_movement_dance.png"
}];
const faqData = [{
  id: "faq-1",
  question: "How is my family's privacy protected?",
  answer: "At The Story Haven, dignity and privacy are paramount. All stories, artwork, and session recordings are kept strictly confidential and are never shared without explicit, written caregiver consent."
}, {
  id: "faq-2",
  question: "Do we need an artistic background to participate?",
  answer: "Not at all. Our programs focus entirely on the process of healing and expression rather than technical skill. We welcome all ages and abilities to explore their creativity freely."
}, {
  id: "faq-3",
  question: "How can we begin a program with you?",
  answer: "Starting is simple. You can reach out through our Contact page for a consultation or visit our Caregiver Corner to explore upcoming workshops and community retreats for your family."
}, {
  id: "faq-4",
  question: "Are programs available for children in hospitals?",
  answer: "Yes. We offer specialized therapeutic video series and adaptable story programs specifically designed to support children and families navigating medical procedures and extended hospital stays."
}];
export default function BenefitsOfStorytelling() {
  const {
    ref: benefitsRef,
    isVisible: benefitsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: waysRef,
    isVisible: waysVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: faqRef,
    isVisible: faqVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="98859" id="article" className="bg-background text-foreground overflow-hidden py-24 sm:py-32">
      
      {}
      <div ref={benefitsRef} className={`mx-auto max-w-7xl px-6 lg:px-8 transition-all duration-1000 ease-out ${benefitsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="mx-auto max-w-2xl text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight font-serif sm:text-5xl text-primary">
            The Healing Power of Story
          </h2>
          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            We believe every child's story matters. Through guided creative expression, we open pathways to emotional resilience and profound family healing.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {benefitsData.map((benefit, i) => {
          const Icon = benefit.icon;
          return <Card key={benefit.id} data-index={i} className="rounded-[40px] bg-secondary/30 backdrop-blur-xl border-white/50 shadow-xl shadow-primary/5 transition-transform hover:-translate-y-2 duration-300">
                <CardHeader className="pb-4 pt-10 px-8">
                  <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-background shadow-[0_0_25px_rgba(var(--color-ring),0.25)]">
                    <Icon className="h-8 w-8 text-ring" />
                  </div>
                  <CardTitle className="font-serif text-2xl text-primary">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent className="px-8 pb-10">
                  <CardDescription className="text-base text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>

      {}
      <div ref={waysRef} className={`mx-auto max-w-7xl px-6 lg:px-8 mt-32 sm:mt-40 transition-all duration-1000 delay-200 ease-out ${waysVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="mx-auto max-w-2xl text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tight font-serif sm:text-4xl text-primary">
            Ways to Find Your Voice
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Explore the creative mediums that feel most natural and safe for your journey.
          </p>
        </div>

        <Tabs defaultValue="written" className="w-full max-w-4xl mx-auto">
          <TabsList className="grid w-full grid-cols-3 rounded-full bg-secondary/40 p-1.5 mb-8 h-auto">
            {mediumsData.map((medium, i) => <TabsTrigger key={medium.id} value={medium.id} data-index={i} className="rounded-full py-3 font-serif text-base data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-lg transition-all">
                {medium.label}
              </TabsTrigger>)}
          </TabsList>

          {mediumsData.map((medium, i) => {
          const Icon = medium.icon;
          return <TabsContent key={medium.id} value={medium.id} data-index={i} className="mt-0 outline-none">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center bg-card/60 backdrop-blur-sm rounded-[40px] p-6 sm:p-10 shadow-2xl border border-white/20">
                  <div className="order-2 md:order-1 space-y-6">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent text-accent-foreground font-medium text-sm">
                      <Icon className="h-4 w-4" />
                      <span>{medium.label}</span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-serif font-bold text-primary">
                      {medium.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed text-lg">
                      {medium.description}
                    </p>
                  </div>
                  <div className="order-1 md:order-2 relative h-64 sm:h-80 rounded-[32px] overflow-hidden bg-muted">
                    <img src={medium.image} alt={medium.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-110" />
                  </div>
                </div>
              </TabsContent>;
        })}
        </Tabs>

        <div className="mt-16 flex justify-center">
          <Button asChild size="lg" className="rounded-full bg-ring hover:bg-ring/90 text-primary-foreground px-8 py-6 text-lg font-medium shadow-[0_0_30px_rgba(var(--color-ring),0.3)] hover:shadow-[0_0_45px_rgba(var(--color-ring),0.5)] transition-all duration-300 group">
            <Link to="/ShareYourStoryPage">
              Start Your Journey
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </div>

      {}
      <div ref={faqRef} className={`mx-auto max-w-4xl px-6 lg:px-8 mt-32 sm:mt-40 transition-all duration-1000 delay-300 ease-out ${faqVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="bg-primary text-primary-foreground rounded-[40px] p-8 sm:p-16 shadow-2xl relative overflow-hidden">
          {}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-ring/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-secondary/20 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tight font-serif sm:text-4xl">
                Frequently Asked Questions
              </h2>
              <p className="mt-4 text-primary-foreground/70">
                Peace of mind as you begin your healing journey with us.
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqData.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-primary-foreground/10 bg-primary-foreground/5 px-6 rounded-3xl border-none data-[state=open]:bg-primary-foreground/10 transition-colors">
                  <AccordionTrigger className="font-serif text-lg hover:no-underline py-6 text-left [&[data-state=open]>svg]:text-ring">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-primary-foreground/80 text-base leading-relaxed pb-6 font-sans">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>

    </section>;
}